import React, { useEffect, useRef, useState } from 'react';
import CaseInfoDisplay from '../shared/CaseInfoDisplay';
import AllRecordings from '../shared/AllRecordings';
import VisitNotesPanel from '../shared/VisitNotesPanel';
import { fetchCaseTranscripts, listCaseAudioSessions } from '../../services/api';
import { getAuthData } from '../../utils/sessionStorage';

/**
 * LiveTranscription - Real-time conversation transcription display
 * Shows speaker segments and live transcript updates
 */
const LiveTranscription = ({
  transcript,
  speakerSegments,
  connectionStatus = 'disconnected',
  sessionType = 'unknown',
  isRecording = false,
  caseId
}) => {
  const transcriptRef = useRef(null);
  const [caseTranscripts, setCaseTranscripts] = useState([]);
  const [audioSessions, setAudioSessions] = useState([]); // For AllRecordings component
  const [activeSessionDetails, setActiveSessionDetails] = useState(null); // For active session
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRecordings, setShowRecordings] = useState(false);
  const [showVisitNotes, setShowVisitNotes] = useState(false);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript, speakerSegments]);

  // Combined effect for fetching case data to avoid duplicate API calls
  useEffect(() => {
    const loadCaseData = async () => {
      // Get case ID from localStorage as single source of truth
      const storedCaseId = localStorage.getItem('currentCaseId') || caseId;
      
      if (!storedCaseId) {
        return;
      }

      const authData = getAuthData();
      if (!authData?.api_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // First get the list of audio sessions (lightweight call)
        const sessionsData = await listCaseAudioSessions(authData.api_token, storedCaseId);
        setAudioSessions(sessionsData.sessions || []);
        
        // Only fetch detailed transcripts if there are sessions
        if (sessionsData.sessions && sessionsData.sessions.length > 0) {
          // Check if there's an active session that needs real-time status
          const activeSession = sessionsData.sessions.find(s => s.status === 'active');
          
          // Get transcript data (this internally calls getAudioSessionStatus for each session)
          const transcriptsData = await fetchCaseTranscripts(authData.api_token, storedCaseId);
          setCaseTranscripts(transcriptsData.transcripts || []);
          
          // For active sessions, we might want to poll for updates separately
          if (activeSession) {
            // Find the detailed status from the transcripts we just fetched
            const activeTranscript = transcriptsData.transcripts?.find(
              t => t.session_id === activeSession.session_id
            );
            if (activeTranscript) {
              setActiveSessionDetails({
                session_id: activeSession.session_id,
                status: activeSession.status,
                segments: activeTranscript.segments
              });
            }
          }
        } else {
          setCaseTranscripts([]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load transcripts');
      } finally {
        setLoading(false);
      }
    };

    loadCaseData();
  }, [caseId]);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Get speaker color based on speaker name
  const getSpeakerColor = (speaker) => {
    const colors = {
      'Provider': '#0e71eb',
      'Patient': '#4caf50',
      'Speaker A': '#ff9800',
      'Speaker B': '#673ab7',
      'Speaker C': '#e91e63'
    };
    return colors[speaker] || '#666';
  };

  // Get session type icon
  const getSessionTypeIcon = () => {
    switch (sessionType) {
      case 'zoom': return '📹';
      case 'twilio': return '📞';
      case 'in-person': return '🎤';
      default: return '🔊';
    }
  };

  // Get connection status info
  const getConnectionStatusInfo = () => {
    switch (connectionStatus) {
      case 'connected':
        return { color: '#4caf50', text: 'Connected', icon: '🟢' };
      case 'connecting':
        return { color: '#ff9800', text: 'Connecting', icon: '🟡' };
      case 'disconnected':
        return { color: '#f44336', text: 'Disconnected', icon: '🔴' };
      case 'error':
        return { color: '#f44336', text: 'Error', icon: '❌' };
      default:
        return { color: '#666', text: 'Unknown', icon: '⚪' };
    }
  };

  const statusInfo = getConnectionStatusInfo();

  return (
    <div className="live-transcription">
      <div className="transcription-header">
        <div className="header-left">
          <h3>
            Live Transcription
          </h3>
          <div className="session-info">
            {getSessionTypeIcon()} <span className="session-type">{sessionType}</span>
          </div>
        </div>
        <div className="transcription-status">
          <div className="connection-status" style={{ color: statusInfo.color }}>
            {statusInfo.icon} {statusInfo.text}
          </div>
          {isRecording && connectionStatus === 'connected' ? (
            <span className="status-active">
              <span className="status-dot"></span>
              Recording
            </span>
          ) : (
            <span className="status-inactive">
              {connectionStatus === 'connected' ? 'Ready' : 'Waiting for connection...'}
            </span>
          )}
        </div>
      </div>

      {/* Case Information Display */}
      <CaseInfoDisplay
        caseId={caseId}
        compact={true}
        showPatientInfo={true}
        showProviderInfo={true}
        showSessionInfo={true}
        className="mx-3 mt-2"
      />

      <div 
        className="transcription-content overflow-auto" 
        ref={transcriptRef}
      >
        {speakerSegments && speakerSegments.length > 0 ? (
          // Show structured speaker segments
          <div className="speaker-segments">
            {speakerSegments.map((segment, index) => (
              <div key={index} className="speaker-segment">
                <div className="segment-header">
                  <span 
                    className="speaker-label"
                    style={{ color: getSpeakerColor(segment.speaker) }}
                  >
                    {segment.speaker || `Speaker ${index + 1}`}
                  </span>
                  {segment.timestamp && (
                    <span className="segment-timestamp">
                      {formatTimestamp(segment.timestamp)}
                    </span>
                  )}
                </div>
                <div className="segment-text">
                  {segment.text || segment.content}
                </div>
              </div>
            ))}
          </div>
        ) : caseTranscripts.length > 0 ? (
          // Show historical transcripts if available
          <div className="historical-transcripts">
            <div className="historical-header">
              <h4>Visit Transcripts ({caseTranscripts.length} sessions)</h4>
            </div>
            {/* Show raw transcript as fallback */}
            {transcript && (
              <div className="raw-transcript">
                <div className="transcript-text">
                  {transcript}
                </div>
              </div>
            )}
            {caseTranscripts.map((session, sessionIndex) => (
              <div key={session.session_id} className="historical-session">
                <div className="session-header">
                  <span className="session-date">
                    {new Date(session.created_at).toLocaleDateString()} at {new Date(session.created_at).toLocaleTimeString()}
                  </span>
                  <span className="session-status">{session.status}</span>
                  {/* {session.visit_note_id && (
                    <button 
                      className="btn btn-success btn-sm ms-2"
                      onClick={() => setShowVisitNotes(true)}
                    >
                      📝 Note
                    </button>
                  )} */}
                </div>
                
                {/* Session-level transcript display */}
                {session.session_transcript && session.session_transcript.transcript_text && (
                  <div className="session-transcript mb-3" style={{ 
                    background: '#f8f9fa', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px', 
                    padding: '16px',
                    marginTop: '10px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h5 style={{ margin: 0, color: '#495057' }}>
                        📝 Full Session Transcript
                      </h5>
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        {session.session_transcript.transcript_text.split(/\s+/).length} words
                      </span>
                    </div>
                    <div style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      fontSize: '14px', 
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      color: '#212529'
                    }}>
                      {session.session_transcript.transcript_text}
                    </div>
                    {session.session_transcript.completed_at && (
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '8px', borderTop: '1px solid #e9ecef', paddingTop: '8px' }}>
                        Transcription completed: {new Date(session.session_transcript.completed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
                {session.segments.map((segment, segmentIndex) => (
                  <div key={`${session.session_id}-${segment.segment_number}`} className="historical-segment">
                    <div className="segment-meta">
                      <span className="segment-source">{segment.source}</span>
                      <span className="segment-duration">
                        {segment.duration_seconds}s
                      </span>
                    </div>
                    <div className="segment-text">
                      {/* Show audio player if S3 URL exists */}
                      {segment.s3_url && (
                        <div className="audio-player-section">
                          <audio 
                            controls 
                            className="w-100 mb-2"
                            preload="metadata"
                            ref={(audioEl) => {
                              // For Twilio recordings, check if playback sounds slow/fast and adjust
                              if (audioEl && segment.source?.toLowerCase().includes('twilio')) {
                                audioEl.addEventListener('loadedmetadata', () => {
                                  // If the audio duration seems off compared to segment.duration_seconds,
                                  // it might indicate a sample rate mismatch
                                  const expectedDuration = segment.duration_seconds;
                                  const actualDuration = audioEl.duration;
                                  
                                  if (expectedDuration && actualDuration) {
                                    const ratio = actualDuration / expectedDuration;
                                    
                                    // If audio is playing at wrong speed, adjust playback rate
                                    if (ratio > 1.5) {
                                      // Audio is too long, speed it up (likely 8kHz played as 16kHz)
                                      audioEl.playbackRate = 2.0;
                                    } else if (ratio < 0.7) {
                                      // Audio is too short, slow it down
                                      audioEl.playbackRate = 0.5;
                                    }
                                  }
                                });
                              }
                            }}
                            onError={(e) => {
                              // Hide the audio player and show error message
                              e.target.style.display = 'none';
                              const errorDiv = e.target.parentElement.querySelector('.audio-error');
                              if (errorDiv) {
                                errorDiv.classList.remove('d-none');
                              }
                            }}
                          >
                            <source src={segment.s3_url} type="audio/mpeg" />
                            <source src={segment.s3_url} />
                            Your browser does not support the audio element.
                          </audio>
                          <div 
                            className="audio-error alert alert-danger small d-none"
                          >
                            <strong>⏰ Recording link expired</strong><br/>
                            Audio recordings are only available for 24 hours after creation.
                          </div>
                          {/* Playback speed control for Twilio recordings */}
                          {segment.source?.toLowerCase().includes('twilio') && (
                            <div className="audio-controls d-flex align-items-center gap-2 mt-2">
                              <small className="text-muted">Playback Speed:</small>
                              <div className="btn-group btn-group-sm" role="group">
                                <button 
                                  type="button" 
                                  className="btn btn-outline-secondary"
                                  onClick={(e) => {
                                    const audio = e.target.closest('.audio-player-section').querySelector('audio');
                                    if (audio) {
                                      audio.playbackRate = 0.5;
                                      // Update active button styling
                                      e.target.parentElement.querySelectorAll('.btn').forEach(btn => btn.classList.remove('active'));
                                      e.target.classList.add('active');
                                    }
                                  }}
                                >
                                  0.5x
                                </button>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-secondary"
                                  onClick={(e) => {
                                    const audio = e.target.closest('.audio-player-section').querySelector('audio');
                                    if (audio) {
                                      audio.playbackRate = 1.0;
                                      e.target.parentElement.querySelectorAll('.btn').forEach(btn => btn.classList.remove('active'));
                                      e.target.classList.add('active');
                                    }
                                  }}
                                >
                                  1x
                                </button>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-secondary active"
                                  onClick={(e) => {
                                    const audio = e.target.closest('.audio-player-section').querySelector('audio');
                                    if (audio) {
                                      audio.playbackRate = 2.0;
                                      e.target.parentElement.querySelectorAll('.btn').forEach(btn => btn.classList.remove('active'));
                                      e.target.classList.add('active');
                                    }
                                  }}
                                >
                                  2x
                                </button>
                              </div>
                              <span className="badge bg-info">8kHz MP3</span>
                            </div>
                          )}
                          {/* Audio format info for debugging */}
                          {/* {!segment.source?.toLowerCase().includes('twilio') && (
                            <div className="audio-info text-muted small mt-1">
                              Source: {segment.source} | Duration: {segment.duration_seconds}s (may be longer than audio duration above)
                            </div>
                          )} */}
                        </div>
                      )}
                      
                      {/* Show any available transcript or status */}
                      <div>
                        {/* <strong>Status:</strong> {segment.transcription_status}<br/> */}
                        {/* <strong>Duration:</strong> {segment.duration_seconds}s<br/> */}
                        {/* <strong>Source:</strong> {segment.source}<br/> */}
                        {/* Note: Individual segments no longer have transcript_text.
                            The full session transcript is available at the session level
                            and will be displayed separately. */}
                        {!segment.transcript_text && segment.transcription_status === 'completed' && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#e8f5e9', borderRadius: '5px', border: '1px solid #4CAF50' }}>
                            <strong style={{ color: '#2E7D32' }}>✅ Transcription Completed!</strong><br/>
                            The audio has been successfully transcribed.<br/>
                            {/* Click the "Notes" tab above to see the full medical documentation. */}
                          </div>
                        )}
                        {segment.transcription_status === 'pending' && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#fff3e0', borderRadius: '5px', border: '1px solid #ff9800' }}>
                            <strong style={{ color: '#E65100' }}>⏳ Transcription Pending</strong><br/>
                            This audio segment is awaiting transcription processing.
                          </div>
                        )}
                        {segment.transcription_status === 'failed' && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#ffebee', borderRadius: '5px', border: '1px solid #f44336' }}>
                            <strong style={{ color: '#C62828' }}>❌ Transcription Failed</strong><br/>
                            There was an error processing this audio segment.<br/>
                            <div id={`retry-message-${session.session_id}-${segment.id}`}></div>
                            <button 
                              className="btn btn-danger btn-sm mt-2"
                              id={`retry-btn-${session.session_id}-${segment.id}`}
                              onClick={async (e) => {
                                const btn = e.currentTarget; // currentTarget always refers to the element with the listener
                                const messageDiv = btn.parentElement.querySelector(`#retry-message-${session.session_id}-${segment.id}`);                                
                                try {
                                  const authData = getAuthData();
                                  if (!authData?.api_token) {
                                    messageDiv.innerHTML = '<div style="margin-top: 10px; padding: 8px; background: #f8d7da; color: #721c24; border-radius: 4px; border: 1px solid #f5c6cb;">Authentication required</div>';
                                    return;
                                  }
                                  
                                  // Show pending message and disable button
                                  btn.disabled = true;
                                  btn.innerHTML = '⏳ Processing...';
                                  messageDiv.innerHTML = '<div style="margin-top: 10px; padding: 8px; background: #fff3cd; color: #856404; border-radius: 4px; border: 1px solid #ffeeba;">Sending audio for transcription...</div>';
                                  
                                  // Use the proper API module
                                  const { triggerTranscription } = await import('../../services/api');
                                  const result = await triggerTranscription(
                                    authData.api_token,
                                    session.session_id,
                                    segment.id
                                  );
                                  
                                  if (result.success) {
                                    // Show success message and hide button
                                    messageDiv.innerHTML = '<div class="text-success my-2">Audio sent for transcription. Please refresh in a few moments.</div>';
                                    btn.style.display = 'none';
                                    
                                    // Optionally reload transcripts after a delay
                                    // setTimeout(() => {
                                    //   window.location.reload();
                                    // }, 3000);
                                  } else {
                                    // Show error message and re-enable button
                                    messageDiv.innerHTML = `<div class="text-danger my-2">Failed to transcribe: ${result.error || 'Unknown error'}</div>`;
                                    btn.disabled = false;
                                    btn.innerHTML = 'Retry Transcription';
                                  }
                                } catch (error) {
                                  // Show error message and re-enable button
                                  messageDiv.innerHTML = `<div class="text-warning my-2">Error retrying transcription: ${error.message}</div>`;
                                  btn.disabled = false;
                                  btn.innerHTML = 'Retry Transcription';
                                }
                              }}
                            >Retry
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : loading ? (
          // Loading state
          <div className="empty-state">
            <div className="empty-icon">⏳</div>
            <p>Loading transcripts...</p>
            <small>Please wait while we fetch your case data</small>
          </div>
        ) : error ? (
          // Error state
          <div className="empty-state">
            <div className="empty-icon">❌</div>
            <p>Failed to load transcripts</p>
            <small>{error}</small>
          </div>
        ) : (
          // Empty state
          <div className="empty-state">
            <div className="empty-icon">🎤</div>
            <p>No transcripts found</p>
            <small>{caseId ? 'Start a recording to see transcripts here' : 'Select a case to view transcripts'}</small>
          </div>
        )}
      </div>

      {/* <div className="transcription-actions">
        <button 
          className="action-button"
          onClick={() => {
            if (transcriptRef.current) {
              transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
            }
          }}
          title="Scroll to bottom"
        >
          ⬇️ Latest
        </button>
        
        <button 
          className="action-button"
          onClick={() => {
            const content = speakerSegments?.length > 0 
              ? speakerSegments.map(seg => `${seg.speaker}: ${seg.text || seg.content}`).join('\n')
              : transcript;
            if (content) {
              navigator.clipboard.writeText(content);
            }
          }}
          title="Copy transcript"
          disabled={!transcript && (!speakerSegments || speakerSegments.length === 0)}
        >
          📋 Copy
        </button>

        <button 
          className="action-button"
          onClick={() => setShowRecordings(!showRecordings)}
          title="Toggle recordings view"
        >
          🎙️ {showRecordings ? 'Hide' : 'Show'} Recordings
        </button>
      </div> */}

      {/* All Recordings Section */}
      {showRecordings && (
        <div className="mt-3">
          <AllRecordings
            existingSessions={audioSessions}
            audioSegments={[]}
            activeSessionDetails={activeSessionDetails}
            loadingSessions={loading}
            onViewNote={(session) => {
              // console.log('View note for session:', session);
              // TODO: Implement view note functionality
            }}
            onClose={() => setShowRecordings(false)}
            formatDuration={(seconds) => {
              const mins = Math.floor(seconds / 60);
              const secs = seconds % 60;
              return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }}
          />
        </div>
      )}

      {/* Visit Notes Side Panel */}
      <VisitNotesPanel
        isOpen={showVisitNotes}
        noteId={null}
        caseId={caseId}
        onClose={() => setShowVisitNotes(false)}
        authData={getAuthData()}
      />

    </div>
  );
};

export default LiveTranscription;